'use client';
import React, { useEffect } from 'react';
import styles from './AdvertiserDisclosure.module.scss';
import Modal from 'react-modal';
import { Icon, Skeleton } from '@benzinga/core-ui';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { ContentManager, PartnerDisclosure } from '@benzinga/content-manager';
import { DEFAULT_PARTNER_DISCLOSURE_FALLBACK } from '../assets/mocks/partner-disclosure';
import { ArticleData } from '@benzinga/article-manager';
import { SessionContext } from '@benzinga/session-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export const AdvertiserDisclosureWrapper = ({
  children,
  className,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={`${styles.wrapper}${className ? ` ${className}` : ''}`}>{children}</div>;

interface Params {
  slug?: string;
  articleData?: ArticleData;
}

export const AdvertiserDisclosure: React.FC<Params> = ({ articleData, slug }) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [disclosure, setDisclosure] = React.useState<PartnerDisclosure | null>(null);
  const disclosureSlug = slug || 'default';
  const session = React.useContext(SessionContext);

  const getPartnerDisclosureParams = React.useCallback(() => {
    const topics = articleData?.terms?.map(item => item.name);

    return {
      short: false,
      topics: topics || [],
    };
  }, [articleData]);

  const setDefaultDisclosure = () => {
    setDisclosure({
      content: DEFAULT_PARTNER_DISCLOSURE_FALLBACK,
      id: 0,
      popup_content: DEFAULT_PARTNER_DISCLOSURE_FALLBACK,
      slug: 'default',
    });
  };

  const assemblePartnerDisclosure = React.useCallback(async () => {
    try {
      const contentManager = session.getManager(ContentManager);

      let partnerDisclosure;
      if (articleData && !slug) {
        partnerDisclosure = await contentManager.getPartnerDisclosureByNodeParams(getPartnerDisclosureParams());
      } else {
        partnerDisclosure = await contentManager.getPartnerDisclosureBySlug(disclosureSlug, { short: false });
      }

      setIsLoading(false);
      const disclosure = partnerDisclosure?.ok;
      disclosure ? setDisclosure(disclosure) : setDefaultDisclosure();
    } catch {
      setIsLoading(false);
      setDefaultDisclosure();
    }
  }, [articleData, disclosureSlug, getPartnerDisclosureParams, session, slug]);

  useEffect(() => {
    if (!disclosure && isModalVisible) {
      assemblePartnerDisclosure();
    }
  }, [isModalVisible, disclosure, disclosureSlug, assemblePartnerDisclosure]);

  return (
    <AdvertiserDisclosureWrapper className="advertiser-disclosure-wrapper">
      <button onClick={() => setIsModalVisible(true)}>Partner Disclosure</button>
      <Modal
        isOpen={isModalVisible}
        style={{
          content: {
            border: 'none',
            bottom: 'auto',
            left: '50%',
            margin: '10px 0',
            marginRight: '-50%',
            maxHeight: 'calc(100% - 50px)',
            maxWidth: '700px',
            padding: '0',
            right: 'auto',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
          },
          overlay: {
            background: 'rgba(51, 51, 51, 0.35)',
          },
        }}
      >
        <div className="bg-white p-4 min-w-[300px] max-w-[800px]">
          <div
            className="absolute right-5 top-5 text-blue-light_30 mb-1 font-medium text-xl cursor-pointer"
            onClick={() => setIsModalVisible(false)}
          >
            <Icon className="text-gray-400" icon={faTimes} />
          </div>
          <h5 className="mb-2">Partner Disclosure</h5>
          <div>
            {isLoading && (
              <>
                <Skeleton active paragraph={{ rows: 7 }} title={false} />
                <Skeleton active paragraph={{ rows: 7 }} title={false} />
              </>
            )}
            {disclosure && <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(disclosure.popup_content) }} />}
          </div>
          <div className="text-right">
            <a
              href={`/partner-disclosure/${disclosure?.slug || disclosureSlug}`}
              rel="nofollow noreferrer"
              target="_blank"
            >
              Read full version
            </a>
          </div>
        </div>
      </Modal>
    </AdvertiserDisclosureWrapper>
  );
};
